/**
 * Config module public API.
 *
 * Holds the resolved client configuration in a module-scoped store and
 * exposes getters with safe defaults. Bootstrap code calls resolveConfig()
 * then setConfig() once; the rest of the app reads via the getters.
 */

import type { ClientConfiguration, ResolveConfigOptions } from './types';
import { ConfigurationError } from './types';
import { resolveOrigin, isRealDomain, LOCAL_API_ORIGIN } from './resolve';
import { fetchConfiguration } from './fetch';
import { mergeConfig } from './merge';

export type { ClientConfiguration, RemoteConfiguration, ResolveConfigOptions } from './types';
export { ConfigurationError } from './types';

const DEFAULT_API_BASE_URL = LOCAL_API_ORIGIN;
const DEFAULT_TELEGRAM_BOT_NAME = 'deaddrop_service_bot';
const DEFAULT_TRACES_SAMPLE_RATE = 0.1;
const DEFAULT_STAGE = 'local';

// ─── Module-scoped store ──────────────────────────────────────────────────────

let _config: ClientConfiguration | null = null;

// Boot-time override origins captured from resolveConfig(). These preserve the
// local-dev divergence between the internal apiUrl and the public proxy host
// (externalApiUrl) / WebSocket host (wsUrl). When unset, the getters derive
// from service.apiUrl, which matches the public host in production.
let _externalApiUrlOverride: string | null = null;
let _wsUrlOverride: string | null = null;

export function setConfig(config: ClientConfiguration): void {
  _config = config;
}

export function getResolvedApiBaseUrl(): string {
  return _config?.service.apiUrl ?? DEFAULT_API_BASE_URL;
}

export function getResolvedExternalApiBaseUrl(): string {
  return _externalApiUrlOverride ?? _config?.service.apiUrl ?? DEFAULT_API_BASE_URL;
}

export function getResolvedWsBaseUrl(): string {
  if (_wsUrlOverride) {
    return _wsUrlOverride;
  }
  const apiUrl = _config?.service.apiUrl;
  if (apiUrl) {
    return apiUrl.replace(/^http/, 'ws');
  }
  if (typeof window !== 'undefined') {
    return window.location.origin.replace(/^http/, 'ws');
  }
  return DEFAULT_API_BASE_URL.replace(/^http/, 'ws');
}

export function getTelegramBotName(): string {
  return _config?.features.telegramBotName ?? DEFAULT_TELEGRAM_BOT_NAME;
}

export function getWaitlistUrl(): string | null {
  return _config?.features.waitlistUrl ?? null;
}

export function getSentryConfig(): {
  dsn: string | null;
  tracesSampleRate: number;
  environment: string;
} {
  return (
    _config?.sentry ?? {
      dsn: null,
      tracesSampleRate: DEFAULT_TRACES_SAMPLE_RATE,
      environment: DEFAULT_STAGE,
    }
  );
}

export function getStage(): string {
  return _config?.service.stage ?? DEFAULT_STAGE;
}

export function getVersion(): string | undefined {
  return _config?.service.version;
}

// ─── Orchestrator ───────────────────────────────────────────────────────────

/**
 * Resolve the full client configuration.
 *
 * Derives the config fetch origin, fetches remote config, and merges all tiers.
 * On production deaddrop domains, fetch failure is fatal — no fallback exists.
 * Throws ConfigurationError when required fields cannot be resolved.
 */
export async function resolveConfig(options: ResolveConfigOptions): Promise<ClientConfiguration> {
  // Capture boot-time override origins so the external/WS getters can prefer
  // them later. Falsy values fall back to derivation from service.apiUrl.
  _externalApiUrlOverride = options.overrides?.externalApiUrl || null;
  _wsUrlOverride = options.overrides?.wsUrl || null;

  const origin = resolveOrigin(options);

  const fetched = await fetchConfiguration(origin);

  const hostname = window.location.hostname;

  if (!fetched && isRealDomain(hostname)) {
    throw new ConfigurationError('Failed to load configuration from server.');
  }

  const conventions = {
    apiUrl: origin,
  };

  const config = mergeConfig(options.overrides, fetched, conventions);

  // On the dev reverse proxy (deaddrop.bezoan.com), the fetched api_url
  // reflects the backend's internal address. Override it with the SPA
  // origin so requests route through the proxy.
  if (!isRealDomain(hostname) && hostname === 'deaddrop.bezoan.com') {
    config.service.apiUrl = window.location.origin;
  }

  return config;
}
